import { ApiReportReportStatusChoices, ReportReviewerKind, ReportReviewerStatus } from "@api/graphql/types-and-hooks";
import { useIsAdmin } from "@components/ReportReview/hooks/useIsAdmin";
import { useMyAuth0Id } from "@components/ReportReview/hooks/useMyAuth0Id";
import { useReportReviewers } from "@components/ReportReview/hooks/useReportReviewers";
import { Reviewer } from "@components/ReportReview/reviewers";
import { useReportOptional } from "@providers/ReportProvider";

/** Whether the viewer wrote the report; one rule for the Reviewers controls and Checkpoint's Waiting on you. */
export const isReportAuthor = (
  report: { createdBy?: { id: string } | null } | undefined,
  myId: string | undefined,
): boolean => !!myId && report?.createdBy?.id === myId;

const useIsReportAuthor = () => {
  const report = useReportOptional()?.report;
  const myId = useMyAuth0Id();
  return isReportAuthor(report, myId);
};

export const useCanManageReview = () => {
  const isAdmin = useIsAdmin();
  const isAuthor = useIsReportAuthor();
  return isAdmin || isAuthor;
};

export const useCanRequestReview = () => {
  const report = useReportOptional()?.report;
  const canManageReview = useCanManageReview();
  const isOpenForReview =
    report?.reportStatus !== ApiReportReportStatusChoices.Generating &&
    report?.reportStatus !== ApiReportReportStatusChoices.Completed;
  return canManageReview && isOpenForReview;
};

export const useCanSetFinalReviewer = () => {
  const canRequestReview = useCanRequestReview();
  const { reviewers } = useReportReviewers();
  return canRequestReview && !reviewers.some((item) => item.type === ReportReviewerKind.FinalApprover);
};

export const useCanWithdrawReviewer = (reviewer: Reviewer) => {
  const isAdmin = useIsAdmin();
  const isAuthor = useIsReportAuthor();
  return isAdmin || (isAuthor && reviewer.status === ReportReviewerStatus.Requested);
};
